import { PATHS } from "@constants/Paths";
import { PeekBox, PeekButton, PeekDialog, PeekDialogActions, PeekDialogContent, PeekLoader, PeekStack, PeekTypography, useTheme, } from "@piiqtechnologies/ui-components";
import { ScanRequestStatus, useAnalytics, useBulkUpdateScanRequests, useRefetch, } from "@piiqtechnologies/ui-lib";
import { format, isToday } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import messages from "../messages.i18n";
import CancelledRequestsList from "./CancelledRequestsList";
import RequestsToBeCancelled from "./RequestsToBeCancelled";
const CancelDialog = ({ scanRequests, onClose, showAllSpacesAlert }) => {
    var _a;
    const theme = useTheme();
    const intl = useIntl();
    const analytics = useAnalytics();
    const [finalScanList, setFinalScanList] = useState(scanRequests);
    const { setRefetch } = useRefetch();
    const [updateScanRequests, updatedRequestsResponse, isLoading, error] = useBulkUpdateScanRequests();
    const navigate = useNavigate();
    const areRequestsCancelled = useMemo(() => {
        var _a;
        return !!((_a = updatedRequestsResponse === null || updatedRequestsResponse === void 0 ? void 0 : updatedRequestsResponse.data) === null || _a === void 0 ? void 0 : _a.length);
    }, [updatedRequestsResponse]);
    const handleCancelRequests = useCallback(() => {
        if (!finalScanList.length)
            return;
        const requestsToCancel = finalScanList.map((request) => ({
            _id: request._id,
            status: ScanRequestStatus.CANCELED,
            reason: request.reason,
            comment: request.comment,
        }));
        updateScanRequests(requestsToCancel);
        analytics.track("Scan Requests - Canceling Requests", {
            requestsIds: requestsToCancel.map((request) => request._id).join(", "),
        });
    }, [finalScanList]);
    useEffect(() => {
        var _a;
        if ((_a = updatedRequestsResponse === null || updatedRequestsResponse === void 0 ? void 0 : updatedRequestsResponse.data) === null || _a === void 0 ? void 0 : _a.length) {
            setRefetch();
        }
    }, [updatedRequestsResponse]);
    const handleRemoveRequestFromList = (requestId) => {
        const updatedList = finalScanList.filter((request) => request._id !== requestId);
        setFinalScanList(updatedList);
    };
    const handleSubmitNewRequest = () => {
        onClose();
        navigate(`../${PATHS.SCAN_REQUESTS_ADD_NEW_REQUEST}`);
    };
    return (React.createElement(PeekDialog, { open: true, onClose: onClose, maxWidth: "sm", title: (_a = finalScanList[0]) === null || _a === void 0 ? void 0 : _a.community.name },
        React.createElement(PeekDialogContent, null,
            React.createElement(PeekStack, { direction: "column", spacing: theme.spacing(3) },
                !areRequestsCancelled && (React.createElement(PeekTypography, { variant: "body1", color: showAllSpacesAlert ? theme.palette.error.main : theme.palette.text.primary }, (() => {
                    var _a, _b;
                    const scheduledOrAvailable = ((_a = finalScanList[0]) === null || _a === void 0 ? void 0 : _a.scheduledFor) || ((_b = finalScanList[0]) === null || _b === void 0 ? void 0 : _b.availableFrom);
                    let dateLabel = "-";
                    if (scheduledOrAvailable) {
                        const dateObj = new Date(scheduledOrAvailable);
                        dateLabel = isToday(dateObj) ? intl.formatMessage(messages.todayText) : format(dateObj, "MM/dd/yy");
                    }
                    return intl.formatMessage(showAllSpacesAlert ? messages.cancelAllSpacesAlert : messages.cancelSpacesMessage, { date: dateLabel });
                })())),
                areRequestsCancelled && (React.createElement(PeekTypography, { variant: "body1", color: theme.palette.text.primary },
                    intl.formatMessage(messages.spacesCanceledMessage),
                    ":")),
                // LOADING SPINNER
                (isLoading && (React.createElement(PeekBox, { sx: { p: theme.spacing(2) }, textAlign: "center", justifyContent: "center" },
                    React.createElement(PeekLoader, null)))) ||
                    // ERROR MESSAGE
                    (error && (React.createElement(PeekBox, { sx: { p: theme.spacing(2) }, textAlign: "center" },
                        React.createElement(PeekTypography, { color: theme.palette.error.main }, intl.formatMessage(messages.updateRequestsErrorMessage))))) ||
                    // REQUESTS LIST TO BE CANCELLED
                    (!areRequestsCancelled && (React.createElement(RequestsToBeCancelled, { requestsToBeCancelled: finalScanList, onRemoveRequest: handleRemoveRequestFromList }))) ||
                    // CANCELLED REQUESTS LIST
                    (areRequestsCancelled && (React.createElement(CancelledRequestsList, { requestsCancelled: (updatedRequestsResponse === null || updatedRequestsResponse === void 0 ? void 0 : updatedRequestsResponse.data) || [] }))))),
        React.createElement(PeekDialogActions, { style: { minWidth: "400px" } },
            React.createElement(PeekStack, { direction: "column", spacing: theme.spacing(2), sx: { width: "100%", alignItems: "center" } },
                React.createElement(PeekButton, { variant: !areRequestsCancelled ? "outlined" : "contained", onClick: !areRequestsCancelled ? handleCancelRequests : onClose, disabled: isLoading, color: !areRequestsCancelled ? "error" : "primary", size: "large", fullWidth: true, sx: { borderColor: theme.palette.grey[100] } }, intl.formatMessage(!areRequestsCancelled ? messages.confirmCancellationText : messages.doneText)),
                React.createElement(PeekTypography, { onClick: !isLoading ? (areRequestsCancelled ? handleSubmitNewRequest : onClose) : undefined, sx: {
                        textDecoration: "underline",
                        cursor: "pointer",
                    } }, !areRequestsCancelled
                    ? intl.formatMessage(messages.nevermindText)
                    : intl.formatMessage(messages.submitNewRequestText))))));
};
export default CancelDialog;
